import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNotifications } from './NotificationContext'
import { useWebSocket } from './WebSocketContext'

const IoTContext = createContext()

export function useIoT() {
  const context = useContext(IoTContext)
  if (!context) {
    throw new Error('useIoT debe ser usado dentro de un IoTProvider')
  }
  return context
}

// Mock data para dispositivos
const mockDevices = [
  {
    id: '1',
    name: 'Termostato Sala',
    type: 'thermostat',
    room: 'Sala de estar',
    status: 'online',
    value: 22.5,
    unit: '°C',
    battery: 85,
    lastUpdate: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 min ago
    actions: ['setTemperature', 'toggle']
  },
  {
    id: '2',
    name: 'Luz Principal',
    type: 'light',
    room: 'Sala de estar',
    status: 'online',
    value: true,
    brightness: 75,
    lastUpdate: new Date(Date.now() - 2 * 60 * 1000).toISOString(), // 2 min ago
    actions: ['toggle', 'setBrightness', 'setColor']
  },
  {
    id: '3',
    name: 'Sensor Puerta',
    type: 'door_sensor',
    room: 'Entrada',
    status: 'online',
    value: false, // cerrada
    battery: 92,
    lastUpdate: new Date(Date.now() - 1 * 60 * 1000).toISOString(), // 1 min ago
    actions: []
  },
  {
    id: '4',
    name: 'Cámara Jardín',
    type: 'camera',
    room: 'Jardín',
    status: 'offline',
    value: false,
    lastUpdate: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 min ago
    actions: ['toggle', 'record']
  },
  {
    id: '5',
    name: 'Humidificador',
    type: 'humidifier',
    room: 'Dormitorio',
    status: 'online',
    value: 45,
    unit: '%',
    battery: null, // conectado a corriente
    lastUpdate: new Date(Date.now() - 10 * 60 * 1000).toISOString(), // 10 min ago
    actions: ['toggle', 'setHumidity']
  }
]

export function IoTProvider({ children }) {
  const [devices, setDevices] = useState(mockDevices)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    lowBatteryDevices: 0
  })
  
  const { showSuccess, showError, showWarning } = useNotifications()
  const { sendMessage, isConnected } = useWebSocket()

  // Calcular estadísticas
  useEffect(() => {
    const total = devices.length
    const online = devices.filter(d => d.status === 'online').length
    const offline = devices.filter(d => d.status === 'offline').length
    const lowBattery = devices.filter(d => d.battery && d.battery < 20).length

    setStats({
      totalDevices: total,
      onlineDevices: online,
      offlineDevices: offline,
      lowBatteryDevices: lowBattery
    })
  }, [devices])

  // Simular actualizaciones en tiempo real
  useEffect(() => {
    const interval = setInterval(() => {
      setDevices(prevDevices => 
        prevDevices.map(device => {
          // Simular cambios aleatorios
          const shouldUpdate = Math.random() < 0.1 // 10% probabilidad

          if (!shouldUpdate) return device

          let updatedDevice = { ...device, lastUpdate: new Date().toISOString() }

          switch (device.type) {
            case 'thermostat':
              updatedDevice.value = +(device.value + (Math.random() - 0.5) * 2).toFixed(1)
              break
            case 'humidifier':
              updatedDevice.value = Math.max(20, Math.min(80, device.value + (Math.random() - 0.5) * 10))
              break
            case 'door_sensor':
              if (Math.random() < 0.05) { // 5% chance de cambio
                updatedDevice.value = !device.value
                showInfo(`${device.name}: ${updatedDevice.value ? 'Abierta' : 'Cerrada'}`)
              }
              break
          }

          return updatedDevice
        })
      )
    }, 10000) // actualizar cada 10 segundos

    return () => clearInterval(interval)
  }, [showSuccess])

  const toggleDevice = async (deviceId) => {
    setLoading(true)
    try {
      const device = devices.find(d => d.id === deviceId)
      if (!device) throw new Error('Dispositivo no encontrado')

      // Simular comando WebSocket
      if (isConnected) {
        sendMessage({
          type: 'device_command',
          deviceId,
          action: 'toggle',
          timestamp: new Date().toISOString()
        })
      }

      // Actualizar estado localmente
      setDevices(prev => prev.map(d => 
        d.id === deviceId 
          ? { 
              ...d, 
              value: d.type === 'light' ? !d.value : d.status === 'online' ? 'offline' : 'online',
              lastUpdate: new Date().toISOString()
            }
          : d
      ))

      showSuccess(`${device.name} ${device.value ? 'apagado' : 'encendido'}`)
    } catch (error) {
      showError(`Error al controlar dispositivo: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const updateDeviceValue = async (deviceId, action, value) => {
    setLoading(true)
    try {
      const device = devices.find(d => d.id === deviceId)
      if (!device) throw new Error('Dispositivo no encontrado')

      // Simular comando WebSocket
      if (isConnected) {
        sendMessage({
          type: 'device_command',
          deviceId,
          action,
          value,
          timestamp: new Date().toISOString()
        })
      }

      // Actualizar estado localmente
      setDevices(prev => prev.map(d => 
        d.id === deviceId 
          ? { 
              ...d, 
              [action === 'setBrightness' ? 'brightness' : 'value']: value,
              lastUpdate: new Date().toISOString()
            }
          : d
      ))

      showSuccess(`${device.name} actualizado`)
    } catch (error) {
      showError(`Error al actualizar dispositivo: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const addDevice = async (deviceData) => {
    setLoading(true)
    try {
      const newDevice = {
        id: Date.now().toString(),
        ...deviceData,
        status: 'online',
        lastUpdate: new Date().toISOString()
      }

      setDevices(prev => [...prev, newDevice])
      showSuccess(`Dispositivo ${deviceData.name} agregado`)
    } catch (error) {
      showError(`Error al agregar dispositivo: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const removeDevice = async (deviceId) => {
    setLoading(true)
    try {
      const device = devices.find(d => d.id === deviceId)
      setDevices(prev => prev.filter(d => d.id !== deviceId))
      showSuccess(`Dispositivo ${device?.name} eliminado`)
    } catch (error) {
      showError(`Error al eliminar dispositivo: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const getDevicesByRoom = (room) => {
    return devices.filter(device => device.room === room)
  }

  const getRooms = () => {
    return [...new Set(devices.map(device => device.room))]
  }

  const value = {
    devices,
    stats,
    loading,
    toggleDevice,
    updateDeviceValue,
    addDevice,
    removeDevice,
    getDevicesByRoom,
    getRooms
  }

  return (
    <IoTContext.Provider value={value}>
      {children}
    </IoTContext.Provider>
  )
}